import { useState, useCallback, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { toast } from 'sonner';
import { TimelineComment } from '@/types/video-validation';
import {
  getVideoCommentsWithBatches,
  mapDbCommentToTimelineComment,
  linkCommentsToBatch,
} from '@/services/data/videoComments';
import { createCommentBatch } from '@/services/data/commentBatches';
import { createFeedbackPurchase } from '@/services/data/feedbackPurchases';
import { notifyFeedbackWebhook } from '@/services/data/feedbackWebhook';

export function useFeedback(
  eventId: string | undefined,
  userId: string | null,
  inlineActiveVersion: number,
) {
  const [feedbackComments, setFeedbackComments] = useState<TimelineComment[]>([]);
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [focusCommentId, setFocusCommentId] = useState<string | null>(null);
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);

  // Reload comments when version changes (only when panel is visible)
  useEffect(() => {
    if (!eventId || !showFeedbackPanel) return;
    setActiveMarkerId(null);
    const loadComments = async () => {
      try {
        const result = await getVideoCommentsWithBatches(eventId, inlineActiveVersion + 1);
        if (result.success && result.data) {
          setFeedbackComments(result.data.map(mapDbCommentToTimelineComment));
        }
      } catch (error) {
        logger.error('[useFeedback] Error reloading feedback comments:', error);
      }
    };
    loadComments();
  }, [inlineActiveVersion, eventId, showFeedbackPanel]);

  const handleOpenFeedbackPanel = async () => {
    if (!eventId) return;
    setShowFeedbackPanel(true);
    try {
      const result = await getVideoCommentsWithBatches(eventId, inlineActiveVersion + 1);
      if (result.success && result.data) {
        setFeedbackComments(result.data.map(mapDbCommentToTimelineComment));
      }
    } catch (error) {
      logger.error('[useFeedback] Error loading feedback comments:', error);
    }
  };

  const handleSendFeedbackFromPanel = async () => {
    if (!userId || isSendingFeedback || !eventId) return;
    const unsent = feedbackComments.filter((c) => c.dbId && !c.batchId && !c.isSent);
    if (unsent.length === 0) return;

    const hasSentBatch = feedbackComments.some(
      (c) => c.batchId !== undefined && c.batchId !== null,
    );
    setIsSendingFeedback(true);
    try {
      const batchResult = await createCommentBatch({
        video_content_id: eventId,
        user_id: userId,
        video_version: inlineActiveVersion + 1,
      });
      if (!batchResult.success || !batchResult.data) throw new Error('Failed to create batch');
      const batchId = batchResult.data.id;
      const commentIds = unsent.map((c) => c.dbId).filter((id): id is string => !!id);
      const linkResult = await linkCommentsToBatch(commentIds, batchId);
      if (!linkResult.success) throw new Error('Failed to link comments');

      notifyFeedbackWebhook(batchId);

      if (hasSentBatch) {
        await createFeedbackPurchase({
          user_id: userId,
          video_content_id: eventId,
          video_comment_batch_id: batchId,
          amount_cents: 5000,
          status: 'pending',
          notes: `Batch de retours complémentaires - ${unsent.length} commentaire(s)`,
        });
      }

      const sentIds = new Set(commentIds);
      setFeedbackComments((prev) =>
        prev.map((c) =>
          c.dbId && sentIds.has(c.dbId) ? { ...c, isSent: true, batchId } : c,
        ),
      );
      toast.success(
        `${unsent.length} retour${unsent.length > 1 ? 's' : ''} envoyé${unsent.length > 1 ? 's' : ''} avec succès`,
      );
    } catch (error) {
      logger.error('[useFeedback] Error sending feedback:', error);
      toast.error("Erreur lors de l'envoi des retours");
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const handleFeedbackCommentsChange = useCallback((updated: TimelineComment[]) => {
    setFeedbackComments(updated);
  }, []);

  return {
    feedbackComments,
    isSendingFeedback,
    focusCommentId,
    setFocusCommentId,
    activeMarkerId,
    setActiveMarkerId,
    showFeedbackPanel,
    setShowFeedbackPanel,
    handleOpenFeedbackPanel,
    handleSendFeedbackFromPanel,
    handleFeedbackCommentsChange,
  };
}
